import { useForm } from 'react-hook-form';

import { updateRemediationDepositInfo } from '@/api/user';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';

import { selectActiveUserAccountTypes } from '@/redux/user-data/selectors';

import {
  AccountDepositInfo,
  RemediationDepositInfo,
} from '@/types/api/user.ts';
import { UserAccountType } from '@/types/user';

type UseRemediationAccountFormOptions<TAccountFields extends object> = {
  defaultAccountValues: TAccountFields;
  toDepositInfo: (
    account: UserAccountType,
    fields: TAccountFields,
  ) => Partial<AccountDepositInfo>;
  onFinished: () => void;
};

export function useRemediationAccountForm<TAccountFields extends object>({
  defaultAccountValues,
  toDepositInfo,
  onFinished,
}: UseRemediationAccountFormOptions<TAccountFields>) {
  const activeAccounts = useAppSelector(selectActiveUserAccountTypes);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<{ accounts: TAccountFields[] }>({
    defaultValues: {
      accounts: activeAccounts.map(() => ({ ...defaultAccountValues })),
    } as { accounts: TAccountFields[] },
    mode: 'onChange',
  });

  const { mutate, isPending, error } = useMutationWithUserData({
    mutationFn: updateRemediationDepositInfo,
    onFinished,
  });

  const onSubmit = handleSubmit(async payload => {
    if (!activeAccounts.length) return;

    const depositInfoArray = activeAccounts.map(
      (account, index) =>
        ({
          accountTypeId: account.id,
          accountNo: account.accountNo,
          ...toDepositInfo(account, payload.accounts[index]),
        }) as RemediationDepositInfo,
    );

    mutate({ payload: depositInfoArray });
  });

  return {
    control,
    fieldErrors: errors,
    isValid,
    isPending,
    error,
    onSubmit,
    activeAccounts,
  };
}
